/**
 * InboxPage — unified workspace с dual-dialogue.
 *
 * Layout: Inbox List | Shadow Dialogue | Real Dialogue
 */

import { useState, useEffect } from 'react';
import { inboxIdToSource } from '../lib/inbox-id';
import { Link } from 'react-router-dom';
import { Spinner, Badge, Button, OnboardingHint } from '../components/ui';
import {
  useInbox,
  useRejectProposal,
  useDialogueProposals,
  type InboxItem,
} from '../api/inbox';
import { useTelegramProposals } from '../api/telegram';
import {
  DialogueWorkspace,
  EmptyWorkspace,
  telegramProposalToProposal,
} from '../components/inbox/DialogueWorkspace';
import { formatDateShort } from '../lib/date-utils';

// ============================================================================
// InboxPage
// ============================================================================

type ChannelFilter = 'all' | 'email' | 'telegram' | 'widget';
type StatusFilter = 'all' | 'has-draft' | 'active';

export function InboxPage(): JSX.Element {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const { data: inboxData, isLoading: inboxLoading } = useInbox({
    channel: channelFilter,
    status: statusFilter,
  });

  const items = inboxData?.items ?? [];
  const selected = items.find(i => i.id === selectedId) ?? null;

  // Keyboard shortcuts: A/E/R
  // A и E уходят событием в ProposalBubble — там живут и действие, и показ
  // его отказа. Здесь остаётся только отклонение: оно ничего не отправляет
  const rejectProposal = useRejectProposal();

  const activeProposalId = useActiveProposalId(selected);

  useEffect(() => {
    function handleKeydown(e: KeyboardEvent) {
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) return;
      // Удержание клавиши — это поток событий, а не одно нажатие: без этой
      // строки «A» отправляла бы запрос за запросом, пока палец на кнопке
      if (e.repeat) return;
      if (!activeProposalId) return;

      if (e.key === 'a' || e.key === 'A') {
        e.preventDefault();
        // Событием, как и правка: принятие живёт в пузыре, там же
        // показывается отказ отправки. Свой вызов мутации отсюда терял его
        window.dispatchEvent(new CustomEvent('proposal-accept', { detail: { proposalId: activeProposalId } }));
      }
      if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('proposal-edit-start', { detail: { proposalId: activeProposalId } }));
      }
      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        rejectProposal.mutate({ proposalId: activeProposalId });
      }
    }
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [activeProposalId, rejectProposal]);

  return (
    <div className="h-full bg-surface-base flex overflow-hidden">
      {/* Inbox List */}
      <div className="w-[280px] flex-shrink-0 bg-surface-base border-r border-border-subtle overflow-hidden flex flex-col">
        <InboxHeader
          channelFilter={channelFilter}
          statusFilter={statusFilter}
          onChannelChange={setChannelFilter}
          onStatusChange={setStatusFilter}
        />
        <div className="flex-1 overflow-y-auto">
          <UnifiedInboxList
            items={items}
            selectedId={selectedId}
            onSelect={setSelectedId}
            isLoading={inboxLoading}
          />
        </div>
      </div>

      {/* Dual-dialogue workspace */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <OnboardingHint hintId="inbox-shortcuts" icon="⌨️" className="mx-4 mt-3 mb-0">
          <strong>Горячие клавиши:</strong> выберите диалог и нажмите{' '}
          <kbd className="px-1.5 py-0.5 rounded bg-surface-subtle border border-border-subtle text-xs font-mono">A</kbd> — принять,{' '}
          <kbd className="px-1.5 py-0.5 rounded bg-surface-subtle border border-border-subtle text-xs font-mono">E</kbd> — редактировать,{' '}
          <kbd className="px-1.5 py-0.5 rounded bg-surface-subtle border border-border-subtle text-xs font-mono">R</kbd> — отклонить.
        </OnboardingHint>
        <div className="flex-1 flex overflow-hidden">
          {selected ? (
            <DialogueWorkspace item={selected} />
          ) : (
            <EmptyWorkspace />
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Inbox Header
// ============================================================================

function InboxHeader({
  channelFilter,
  statusFilter,
  onChannelChange,
  onStatusChange,
}: {
  channelFilter: ChannelFilter;
  statusFilter: StatusFilter;
  onChannelChange: (f: ChannelFilter) => void;
  onStatusChange: (f: StatusFilter) => void;
}): JSX.Element {
  return (
    <div className="px-4 py-3 border-b border-border-subtle space-y-2">
      <h2 className="font-heading font-semibold text-text-primary">Входящие</h2>
      <div className="flex gap-1">
        {(['all', 'email', 'telegram', 'widget'] as const).map(ch => (
          <button
            key={ch}
            onClick={() => onChannelChange(ch)}
            className={`px-2 py-1 text-xs rounded-comp transition-colors ${
              channelFilter === ch
                ? 'bg-accent-brand text-white'
                : 'text-text-secondary hover:bg-surface-subtle'
            }`}
          >
            {ch === 'all' ? 'Все' : ch === 'email' ? 'Email' : ch === 'telegram' ? 'Telegram' : 'Widget'}
          </button>
        ))}
      </div>
      <div className="flex gap-1">
        {(['all', 'has-draft', 'active'] as const).map(st => (
          <button
            key={st}
            onClick={() => onStatusChange(st)}
            className={`px-2 py-1 text-xs rounded-comp transition-colors ${
              statusFilter === st
                ? 'bg-accent-brand text-white'
                : 'text-text-secondary hover:bg-surface-subtle'
            }`}
          >
            {st === 'all' ? 'Все' : st === 'has-draft' ? 'С draft' : 'Активные'}
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Unified Inbox List
// ============================================================================

function UnifiedInboxList({
  items,
  selectedId,
  onSelect,
  isLoading,
}: {
  items: InboxItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  isLoading: boolean;
}): JSX.Element {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Spinner size="md" className="text-text-secondary" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="p-6 text-center text-text-secondary">
        <p className="font-medium">Нет диалогов</p>
        <p className="text-sm mt-1 mb-4">Подключите канал, чтобы начать получать сообщения</p>
        <Link to="/channels">
          <Button variant="primary" size="sm">
            Подключить канал
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1 px-2 py-2">
      {items.map(item => (
        <button
          key={item.id}
          onClick={() => onSelect(item.id)}
          className={`px-3 py-2.5 text-left rounded-card border transition-all duration-150 ${
            selectedId === item.id
              ? 'bg-surface-elevated border-border-strong shadow-sm'
              : 'bg-transparent border-transparent hover:bg-surface-subtle hover:border-border-subtle'
          }`}
        >
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2 min-w-0">
              <Badge
                variant={item.channel === 'email' ? 'active' : item.channel === 'widget' ? 'waiting' : 'ai'}
              >
                {item.channel === 'email' ? 'Email' : item.channel === 'telegram' ? 'TG' : 'Web'}
              </Badge>
              <span className="font-heading font-medium text-sm text-text-primary truncate">
                {item.title}
              </span>
              {item.hasDraft && (
                <div className="w-2 h-2 rounded-full bg-accent-ai flex-shrink-0" title="Есть draft" />
              )}
              {item.needsReply && !item.hasDraft && (
                <div className="w-2 h-2 rounded-full bg-accent-brand flex-shrink-0" title="Ожидает ответа" />
              )}
              {item.hasFailedMessages && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-accent-danger/10 text-accent-danger border border-accent-danger/20 flex-shrink-0" title="Ошибка отправки">
                  ✕
                </span>
              )}
            </div>
            <span className="text-xs text-text-secondary flex-shrink-0 ml-2">
              {formatDateShort(item.lastMessageAt)}
            </span>
          </div>
          {item.snippet && (
            <p className="text-xs text-text-secondary truncate">
              {item.snippet}
            </p>
          )}
        </button>
      ))}
    </div>
  );
}

// ============================================================================
// Hook: get active proposal ID for keyboard shortcuts
// ============================================================================

function useActiveProposalId(item: InboxItem | null): string | null {
  const emailProposals = useDialogueProposals(
    'email',
    item?.channel === 'email' ? item.id : '',
    item?.channel === 'email',
  );
  const source = item ? inboxIdToSource(item) : null;
  const tgChatId = source?.sourceType === 'telegram' ? Number(source.sourceId) : NaN;
  const tgProposals = useTelegramProposals(
    item?.channel === 'telegram' && !isNaN(tgChatId) ? tgChatId : null,
  );
  const widgetSessionId = source?.sourceType === 'widget' ? source.sourceId : '';
  const widgetProposals = useDialogueProposals(
    'widget',
    widgetSessionId,
    item?.channel === 'widget',
  );

  let proposals;
  if (item?.channel === 'email') {
    proposals = emailProposals.data ?? [];
  } else if (item?.channel === 'widget') {
    proposals = widgetProposals.data ?? [];
  } else {
    proposals = (tgProposals.data ?? []).map(telegramProposalToProposal);
  }

  const activeDraft = proposals.find(
    p => p.status === 'ready' && (p.shadowDecision === 'allow' || p.shadowDecision === 'soften'),
  );

  return activeDraft?.id ?? null;
}
